import React, { FunctionComponent } from "react";
import { connect, MapDispatchToProps, MapStateToProps } from "react-redux";

import { CONST_MAX_SCALE_INDEX, CONST_MIN_SCALE_INDEX, CONST_SCALE_STEP, createChangeScaleIndexAction, createRequestPaintingScrollPositionAction, getScaleIndex } from "@multiweb/picasso";
import { ButtonBackgroundColor, ComponentSize, IconButton, TextButton, TextColor } from "@multiweb/reactComponents";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { IconCode } from "@multiweb/types/contracts";
import { IAppState } from "@multiweb/types/react";
import { createToggleGridAction } from "../../../../workflowEditor/actions";
import { LOC_RESET, LOC_TOGGLE_GRID, LOC_ZOOM_IN, LOC_ZOOM_OUT } from "../../../../workflowEditor/locKeys";
import { getPicassoState } from "../../../../workflowEditor/selectors";
import { WorkflowEditorProps } from "../../../../workflowEditor/types";
import { ToolbarSection } from "../components";

export type PaintingHandlingSectionComponentProps = {
    readonly scaleIndex: number;
    readonly changeScaleIndex: (newScaleIndex: number) => void;
    readonly toggleGrid: () => void;
    readonly resetPaintingOffset: () => void;
    readonly switchGridText: string;
    readonly resetText: string;
    readonly zoomInText: string;
    readonly zoomOutText: string;
};

const PaintingHandlingSectionComponent: FunctionComponent<PaintingHandlingSectionComponentProps> = ({
    scaleIndex,
    changeScaleIndex,
    toggleGrid,
    resetPaintingOffset,
    switchGridText,
    resetText,
    zoomInText,
    zoomOutText,
}) => {
    const zoomOut = () => {
        changeScaleIndex(scaleIndex - CONST_SCALE_STEP);
    };

    const zoomIn = () => {
        changeScaleIndex(scaleIndex + CONST_SCALE_STEP);
    };

    const resetPainting = () => {
        resetPaintingOffset();
        changeScaleIndex(1);
    };

    return (
        <ToolbarSection>
            <IconButton iconCode={IconCode.toolGrid} outline={false} onClick={toggleGrid} title={switchGridText} backgroundColor={ButtonBackgroundColor.transparent} />
            <IconButton iconCode={IconCode.uiSearchMinus}outline={false} onClick={zoomOut} disabled={scaleIndex === CONST_MIN_SCALE_INDEX} title={zoomOutText} backgroundColor={ButtonBackgroundColor.transparent} />
            <p>{scaleIndex.toFixed(1).toString()}</p>
            <IconButton iconCode={IconCode.uiSearchPlus} outline={false} onClick={zoomIn} disabled={scaleIndex === CONST_MAX_SCALE_INDEX} title={zoomInText} backgroundColor={ButtonBackgroundColor.transparent} />
            <TextButton onClick={resetPainting} outline={false} text={resetText} title={resetText} size={ComponentSize.large} color={TextColor.blue} />
        </ToolbarSection>
    );
};

type StateProps = Pick<PaintingHandlingSectionComponentProps, "scaleIndex" | "switchGridText" | "resetText" | "zoomInText" | "zoomOutText">;

const mapStateToProps: MapStateToProps<StateProps, WorkflowEditorProps, IAppState> = (state, { hostKey }) => ({
    scaleIndex: getScaleIndex(getPicassoState(state, hostKey)),
    switchGridText: getPrimaryText(state, LOC_TOGGLE_GRID) ?? "Přepnout mřížku",
    resetText: getPrimaryText(state, LOC_RESET) ?? "Resetovat",
    zoomInText: getPrimaryText(state, LOC_ZOOM_IN) ?? "Přiblížit",
    zoomOutText: getPrimaryText(state, LOC_ZOOM_OUT) ?? "Oddálit",
});

type DispatchProps = Pick<PaintingHandlingSectionComponentProps, "changeScaleIndex" | "toggleGrid" | "resetPaintingOffset">;

const mapDispatchToProps: MapDispatchToProps<DispatchProps, WorkflowEditorProps> = (dispatch, { hostKey }) => ({
    changeScaleIndex: (newScaleIndex) => dispatch(createChangeScaleIndexAction(hostKey, newScaleIndex)),
    toggleGrid: () => dispatch(createToggleGridAction(hostKey)),
    resetPaintingOffset: () => dispatch(createRequestPaintingScrollPositionAction(hostKey, { left: 0, top: 0 })),
});

export const PaintingHandlingSection = connect(mapStateToProps, mapDispatchToProps)(PaintingHandlingSectionComponent);
